<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'
import { useTranslations } from '@/composables/translations'
import { usePrintTemplates, pickPrintTemplateSettings } from '@/composables/print-template'
import { usePrintTemplateDeleteOptions } from '@/composables/delete-options'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import EditModeToggle from '@/components/shared/EditModeToggle.vue'
import DeleteOptionsDialog from '@/components/shared/DeleteOptionsDialog.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import PrintTemplateThumbnail from '@/components/accounting/PrintTemplateThumbnail.vue'
import PrintTemplateEdit from '@/components/accounting/PrintTemplateEdit.vue'
import PrintLayoutPicker from '@/components/accounting/PrintLayoutPicker.vue'
import TemplateCardGrid from '@/components/shared/TemplateCardGrid.vue'

const emit = defineEmits(['cancel', 'defaults-changed'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const { notifySuccess } = useNotify()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const { withTranslatedFields } = useTranslations()
const { templates, layouts, defaultFields, templateById, defaultIdFor, isDefaultFor, isDefault, defaultDocumentTypes, documentTypeLabel, setDefaultLabel } = usePrintTemplates()
const { QUOTE, INVOICE } = accountingStore.printDocumentTypeEnum

const filterEnum = Object.freeze({ ALL: 0, AVAILABLE: 1, HIDDEN: 2 })

const filter = ref(filterEnum.ALL)
const editMode = ref(false)
const gridRef = ref(null)
const editingTemplate = ref(null)
const newTemplate = ref(null)
const templateToDelete = ref(null)

const reloadAsync = () => accountingStore.fetchDocumentPrintTemplatesAsync({}, true)
onMounted(reloadAsync)

const availableCount = computed(() => templates.value.filter(template => template.is_available).length)
const hiddenCount = computed(() => templates.value.length - availableCount.value)
const filterOptions = computed(() => [
  { value: filterEnum.ALL, label: `${i18n.t('common.all')} · ${templates.value.length}` },
  { value: filterEnum.AVAILABLE, label: `${i18n.t('common.available')} · ${availableCount.value}` },
  { value: filterEnum.HIDDEN, label: `${i18n.t('common.hidden')} · ${hiddenCount.value}` },
])
const filteredTemplates = computed(() => templates.value.filter(template =>
  filter.value === filterEnum.ALL || template.is_available === (filter.value === filterEnum.AVAILABLE)
))
const availabilityInfo = computed(() => i18n.t('info.printTemplatesAvailability', {
  available: availableCount.value,
  total: templates.value.length,
  quoteDefault: templateById(defaultIdFor(QUOTE))?.name,
  invoiceDefault: templateById(defaultIdFor(INVOICE))?.name,
}))

const lockReason = (template) => {
  if (isDefault(template)) return i18n.t('error.validation.printTemplateDefaultUnavailable')
  if (template.is_available && availableCount.value === 1) return i18n.t('error.validation.printTemplateLastAvailable')
  return null
}

const usageLabel = (template) => i18n.t('common.printTemplateUsage', { quotes: template.quote_count ?? 0, invoices: template.invoice_count ?? 0 })
const layoutName = (template) => layouts.value.find(layout => layout.id === template.layout)?.name
const layoutTemplate = (layout) => templates.value.find(template => template.layout === layout) ?? { layout }


const onToggleAvailable = async (template, isAvailable) => {
  await accountingStore.upsertDocumentPrintTemplateAsync({ id: template.id, is_available: isAvailable })
  await reloadAsync()
}

const onSetDefault = async (template, documentType) => {
  if (!template.is_available) {
    await accountingStore.upsertDocumentPrintTemplateAsync({ id: template.id, is_available: true })
  }
  await accountingStore.updateCompanyDataAsync({ id: accountingStore.companyData.id, [defaultFields[documentType]]: template.id })
  await Promise.all([accountingStore.fetchCompanyDataAsync(null, true), reloadAsync()])
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  emit('defaults-changed')
}

const createAsync = async (payload) => {
  const { data } = await accountingStore.upsertDocumentPrintTemplateAsync({ ...payload, is_available: false })
  await reloadAsync()
  filter.value = filterEnum.ALL
  notifySuccess(i18n.t('info.createdSuccessfully'))
  highlightRowAsync(data.id, gridRef)
  return data
}

const onNew = () => {
  newTemplate.value = { name: i18n.t('common.printTemplateNew'), layout: accountingStore.documentPrintLayoutEnum.CLASSIC }
}

const onNewConfirmed = async () => {
  const { name, layout } = newTemplate.value
  newTemplate.value = null
  await createAsync({ layout, translations: withTranslatedFields({ name }) })
}

const onDuplicate = async (template) => {
  const name = i18n.t('common.copyOf', { name: template.name })
  const languages = [...Object.keys(template.translations), portalStore.selectedLanguage, window.config.DEFAULT_LANGUAGE]
  const translations = Object.fromEntries(languages.map(language => [language, { ...template.translations[language], name }]))
  const copy = await createAsync({ ...pickPrintTemplateSettings(template), translations })
  editingTemplate.value = templateById(copy.id)
}

const { deleteOptions, onDeleteOptionSelect, deleteAsync } = usePrintTemplateDeleteOptions({
  onDeleted: async () => {
    editingTemplate.value = null
    await reloadAsync()
  },
})

const onDeleteConfirmed = () => deleteAsync(templateToDelete.value)

const onEditUpdated = async () => {
  editingTemplate.value = null
  await reloadAsync()
}
</script>
<template>
  <q-card class="dialog-card-flex">
    <navigate-back :text="i18n.t('common.printTemplate', 2)" @cancel="emit('cancel')">
      <template #right>
        <edit-mode-toggle v-if="isMobile && templates.length" v-model="editMode" />
        <q-btn
          style="padding: 0;"
          size="md"
          unelevated
          ripple
          rounded
          icon="mdi-plus-circle-outline"
          @click="onNew"
        />
      </template>
    </navigate-back>
    <q-card-section class="row items-center q-gutter-sm q-py-sm">
      <q-btn-toggle
        v-model="filter"
        :options="filterOptions"
        no-caps
        unelevated
        toggle-color="primary"
        class="print-templates-filter"
      />
    </q-card-section>
    <q-card-section class="q-pt-none q-pb-sm">
      <info-box :message="availabilityInfo" />
    </q-card-section>
    <q-card-section ref="gridRef" class="scroll col" :class="isMobile ? 'q-pa-none' : 'bg-grey-2'">
      <q-list v-if="isMobile">
        <q-item
          v-for="template in filteredTemplates"
          :key="template.id"
          class="q-py-sm q-pl-sm q-pr-md"
          :class="{ 'row-highlight': template.id === highlightedRowId }"
          :clickable="editMode"
          @click="editMode && (editingTemplate = template)"
        >
          <q-item-section avatar>
            <print-template-thumbnail
              class="print-template-row-thumbnail"
              :class="{ 'is-hidden': !template.is_available }"
              :template="template"
              :document-type="QUOTE"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ template.name }}</q-item-label>
            <q-item-label caption>{{ layoutName(template) }} · {{ usageLabel(template) }}</q-item-label>
            <q-item-label v-if="defaultDocumentTypes(template).length">
              <q-chip
                v-for="documentType in defaultDocumentTypes(template)"
                :key="documentType"
                dense
                size="sm"
                color="primary"
                text-color="white"
                icon="mdi-star"
                :label="documentTypeLabel(documentType)"
              />
            </q-item-label>
          </q-item-section>
          <q-item-section side>
            <q-toggle
              :model-value="template.is_available"
              :disable="!!lockReason(template)"
              dense
              @click.stop
              @update:model-value="value => onToggleAvailable(template, value)"
            />
          </q-item-section>
          <q-item-section v-if="editMode" side class="q-pl-sm">
            <q-btn
              v-if="!isDefault(template)"
              style="padding: 0;"
              size="md"
              flat
              rounded
              color="negative"
              icon="mdi-minus-circle-outline"
              @click.stop="templateToDelete = template"
            />
          </q-item-section>
        </q-item>
      </q-list>
      <template-card-grid
        v-else
        :items="filteredTemplates"
        :highlighted-id="highlightedRowId"
        :lock-reason="lockReason"
        :available-label="i18n.t('common.printTemplateAvailableToUsers')"
        @open="template => editingTemplate = template"
        @toggle-available="onToggleAvailable"
      >
        <template #badges="{ item: template }">
          <q-chip
            v-for="documentType in defaultDocumentTypes(template)"
            :key="documentType"
            dense
            size="sm"
            color="primary"
            text-color="white"
            icon="mdi-star"
            :label="documentTypeLabel(documentType)"
          />
        </template>
        <template #paper="{ item: template }">
          <print-template-thumbnail :template="template" :document-type="QUOTE" />
        </template>
        <template #caption="{ item: template }">{{ layoutName(template) }} · {{ usageLabel(template) }}</template>
        <template #menu="{ item: template }">
          <q-item clickable @click="editingTemplate = template">
            <q-item-section avatar><q-icon name="mdi-pencil-outline" /></q-item-section>
            <q-item-section>{{ i18n.t('common.edit') }}</q-item-section>
          </q-item>
          <q-item clickable @click="onDuplicate(template)">
            <q-item-section avatar><q-icon name="mdi-content-copy" /></q-item-section>
            <q-item-section>{{ i18n.t('common.duplicate') }}</q-item-section>
          </q-item>
          <q-item
            v-for="documentType in [QUOTE, INVOICE]"
            :key="documentType"
            clickable
            :disable="isDefaultFor(template, documentType)"
            @click="onSetDefault(template, documentType)"
          >
            <q-item-section avatar><q-icon name="mdi-star-outline" /></q-item-section>
            <q-item-section>{{ setDefaultLabel(documentType) }}</q-item-section>
          </q-item>
          <q-item clickable :disable="isDefault(template)" @click="templateToDelete = template">
            <q-item-section avatar><q-icon name="mdi-delete-outline" color="negative" /></q-item-section>
            <q-item-section class="text-negative">{{ i18n.t('common.delete') }}</q-item-section>
          </q-item>
        </template>
      </template-card-grid>
    </q-card-section>

    <q-dialog
      :model-value="!!newTemplate"
      class="dialog-xs"
      persistent
      @hide="newTemplate = null"
    >
      <q-card v-if="newTemplate">
        <q-card-section class="text-h6">{{ i18n.t('common.printTemplateNew') }}</q-card-section>
        <q-card-section class="q-pt-none">
          <q-input v-model="newTemplate.name" :label="i18n.t('common.name')" autofocus />
          <div class="text-caption text-grey-7 q-mt-md q-mb-xs">{{ i18n.t('common.printTemplateStartFromLayout') }}</div>
          <print-layout-picker v-model="newTemplate.layout" :template-for="layoutTemplate" />
          <info-box class="q-mt-md" :message="i18n.t('info.printTemplateCreatedHidden')" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat :label="i18n.t('common.cancel')" @click="newTemplate = null" />
          <q-btn
            flat
            color="primary"
            :label="i18n.t('common.create')"
            :disable="!newTemplate.name?.trim()"
            @click="onNewConfirmed"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog
      :model-value="!!editingTemplate"
      class="dialog-sm"
      :maximized="isMobile"
      persistent
      @hide="editingTemplate = null"
    >
      <print-template-edit
        v-if="editingTemplate"
        :key="editingTemplate.id"
        :model-value="editingTemplate"
        @cancel="editingTemplate = null"
        @update="onEditUpdated"
        @delete="templateToDelete = editingTemplate"
        @duplicate="onDuplicate(editingTemplate)"
        @set-default="documentType => onSetDefault(editingTemplate, documentType)"
      />
    </q-dialog>

    <confirm-dialog
      :model-value="!!templateToDelete"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @update:model-value="value => { if (!value) templateToDelete = null }"
      @confirm="onDeleteConfirmed"
    />
    <delete-options-dialog
      v-model="deleteOptions.show"
      :title="deleteOptions.title"
      :message="deleteOptions.message"
      :caption="deleteOptions.caption"
      :options="deleteOptions.options"
      @select="onDeleteOptionSelect"
    />
  </q-card>
</template>
<style scoped lang="scss">
.print-template-row-thumbnail {
  width: 3.25rem;

  &.is-hidden {
    opacity: .5;
    filter: grayscale(.8);
  }
}
</style>
